import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import {clientSlider} from "../../constants/data";
import SlideUp from "../ui/SlideUp";

const ClientsSection = () => {
  const firstRowRef = useRef(null);
  const secondRowRef = useRef(null);
  const thirdRowRef = useRef(null);
  const animationRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Function to initialize and animate each row
    const animateRow = (rowRef, direction = 'left') => {
      const row = rowRef.current;
      const items = row.querySelectorAll('.client-item');
      const itemWidth = items[0]?.offsetWidth || 200;
      const gap = 32; // gap-8 = 32px
      const totalWidth = (itemWidth + gap) * items.length;

      // Duplicate items for seamless looping
      items.forEach(item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        row.appendChild(clone);
      });

      // Set initial position
      if (direction === 'right') {
        gsap.set(row, { x: -totalWidth / 2 });
      }

      // Create animation
      const xMovement = direction === 'left' ? -totalWidth / 2 : totalWidth / 2;
      const duration = totalWidth / 100; // Adjust speed as needed

      return gsap.to(row, {
        x: `+=${xMovement}`,
        duration: duration,
        ease: 'none',
        repeat: -1, // Infinite repeat
        modifiers: {
          x: gsap.utils.unitize(x => {
            // Reset position when halfway through for seamless loop
            const xNum = parseFloat(x);
            if (direction === 'left' && xNum <= -totalWidth / 2) {
              return 0;
            }
            if (direction === 'right' && xNum >= 0) {
              return -totalWidth / 2;
            }
            return x;
          })
        }
      });
    };

    // Initialize animations
    const firstAnimation = animateRow(firstRowRef, 'left'); // Right to left
    const secondAnimation = animateRow(secondRowRef, 'right'); // Left to right
    const thirdAnimation = animateRow(thirdRowRef, 'left'); // Right to left

    animationRef.current = {
      first: firstAnimation,
      second: secondAnimation,
      third: thirdAnimation
    };

    // Clean up on unmount
    return () => {
      if (animationRef.current) {
        animationRef.current.first.kill();
        animationRef.current.second.kill();
        animationRef.current.third.kill();
      }
    };
  }, []);

  return (
    <div>
      <section
        className="pb-32 pt-20 overflow-hidden bg-gray-5000"
      >
        <div className="md:w-theme-container w-mobile-container mx-auto mb-12">
          <SlideUp>
            <div className="text-left text-2xl">{t('common.trustworthy')}</div>
            <div className="text-left text-base">{t('common.clientMiniDesc')}</div>
          </SlideUp>
        </div>
        {/* First Row - Right to Left (Infinite) */}
        <SlideUp>
          <div
            ref={firstRowRef}
            className="flex gap-8 mb-8 whitespace-nowrap"
          >
            {clientSlider.first.map(client => (
              <div
                key={`first-${client.id}`}
                className="client-item inline-flex bg-white p-3 overflow-hidden rounded-lg shadow-md items-center justify-center min-w-[200px] h-[110px]"
              >
                {/*<span className="text-lg font-medium">{client.title}</span>*/}
                {client.imgName && (
                  <img
                    src={require(`../../assets/images/clients/row1/${client.imgName}`)}
                    alt={client.title}
                    className="w-full h-auto object-contain"
                  />
                )}
              </div>
            ))}
          </div>
        </SlideUp>

        {/* Second Row - Left to Right (Infinite) */}
        <SlideUp>
          <div
            ref={secondRowRef}
            className="flex gap-8 mb-8 whitespace-nowrap"
          >
            {clientSlider.second.map(client => (
              <div
                key={`second-${client.id}`}
                className="client-item inline-flex bg-white p-3 overflow-hidden rounded-lg shadow-md items-center justify-center min-w-[200px] h-[110px]"
              >
                {client.imgName && (
                  <img
                    src={require(`../../assets/images/clients/row2/${client.imgName}`)}
                    alt={client.title}
                    className="w-full h-auto object-contain"
                  />
                )}
              </div>
            ))}
          </div>
        </SlideUp>

        {/* Third Row - Right to Left (Infinite) */}
        <SlideUp>
          <div
            ref={thirdRowRef}
            className="flex gap-8 whitespace-nowrap"
          >
            {clientSlider.third.map(client => (
              <div
                key={`third-${client.id}`}
                className="client-item inline-flex bg-white p-3 overflow-hidden rounded-lg shadow-md items-center justify-center min-w-[200px] h-[110px]"
              >
                {client.imgName && (
                  <img
                    src={require(`../../assets/images/clients/row3/${client.imgName}`)}
                    alt={client.title}
                    className="w-full h-auto object-contain"
                  />
                )}
              </div>
            ))}
          </div>
        </SlideUp>
      </section>
    </div>

  );
};

export default ClientsSection;
